// @tennis-matches/ui-components/loading-overlay
// Token-based loading overlay. Each startLoading returns a token; stopLoading(token) cancels that request.

import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { BallmatesFullPageLoader } from "../ballmates-loader/BallmatesLoader.jsx";
import { START_EVENT, STOP_EVENT } from "./loadingOverlay.events.js";

const __DEV__ = typeof process !== "undefined" && process.env?.NODE_ENV !== "production";

function devLog(...args) {
  if (__DEV__) {
    console.log("[LoadingOverlay]", ...args);
  }
}

/**
 * LoadingOverlayProvider – token-based loading overlay.
 * activeCount = activeTokens.size (no stale closure).
 *
 * API: startLoading() returns token, stopLoading(token) cancels.
 */
export function LoadingOverlayProvider({ children, t }) {
  const [activeCount, setActiveCount] = useState(0);
  const [message, setMessage] = useState(undefined);

  const pendingTimersRef = useRef(new Map());
  const activeTokensRef = useRef(new Set());
  const shownAtRef = useRef(new Map());
  const minShowMsByTokenRef = useRef(new Map());
  const hideTimersRef = useRef(new Map());

  const updateShowRef = useRef(null);

  useEffect(() => {
    const updateShow = () => {
      const active = activeTokensRef.current;
      const size = active.size;
      setActiveCount(size);
      if (size === 0) setMessage(undefined);
    };
    updateShowRef.current = updateShow;

    const onStart = (e) => {
      const detail = e?.detail ?? {};
      const token = detail.token;
      const msg = detail.message;
      const delayMs = Number(detail.delayMs ?? 250);
      const minShowMs = Number(detail.minShowMs ?? 300);

      if (!token) return;
      devLog("onStart", { token: token.slice(0, 8), delayMs, minShowMs });

      const doShow = () => {
        if (delayMs > 0 && !pendingTimersRef.current.has(token)) return;
        pendingTimersRef.current.delete(token);
        activeTokensRef.current.add(token);
        shownAtRef.current.set(token, Date.now());
        minShowMsByTokenRef.current.set(token, minShowMs);
        if (msg) setMessage(msg);
        updateShowRef.current?.();
        devLog("onStart: shown", { token: token.slice(0, 8), activeSize: activeTokensRef.current.size });
      };

      if (delayMs <= 0) {
        doShow();
      } else {
        const timeoutId = setTimeout(doShow, delayMs);
        pendingTimersRef.current.set(token, timeoutId);
      }
    };

    const onStop = (e) => {
      const detail = e?.detail ?? {};
      const token = detail.token;
      if (!token) return;

      const pendingTimers = pendingTimersRef.current;
      const activeTokens = activeTokensRef.current;
      const shownAt = shownAtRef.current;
      const minShowMsByToken = minShowMsByTokenRef.current;
      const hideTimers = hideTimersRef.current;

      if (pendingTimers.has(token)) {
        const tid = pendingTimers.get(token);
        if (tid) clearTimeout(tid);
        pendingTimers.delete(token);
        devLog("onStop: cancelled pending", { token: token.slice(0, 8) });
        return;
      }

      if (!activeTokens.has(token)) {
        devLog("onStop: token not found (already stopped?)", { token: token.slice(0, 8) });
        return;
      }

      const ts = shownAt.get(token) ?? 0;
      const minShow = minShowMsByToken.get(token) ?? 300;
      const elapsed = Date.now() - ts;
      const remain = minShow - elapsed;

      const removeToken = () => {
        activeTokens.delete(token);
        shownAt.delete(token);
        minShowMsByToken.delete(token);
        hideTimers.delete(token);
        updateShowRef.current?.();
        devLog("onStop: removed active", { token: token.slice(0, 8), activeSize: activeTokens.size });
      };

      if (remain > 0) {
        const hideTimerId = setTimeout(removeToken, remain);
        hideTimers.set(token, hideTimerId);
        devLog("onStop: delayed remove", { token: token.slice(0, 8), remainMs: remain });
      } else {
        removeToken();
      }
    };

    window.addEventListener(START_EVENT, onStart);
    window.addEventListener(STOP_EVENT, onStop);
    return () => {
      window.removeEventListener(START_EVENT, onStart);
      window.removeEventListener(STOP_EVENT, onStop);
      pendingTimersRef.current.forEach((id) => id && clearTimeout(id));
      pendingTimersRef.current.clear();
      hideTimersRef.current.forEach((id) => clearTimeout(id));
      hideTimersRef.current.clear();
    };
  }, []);

  const show = activeCount > 0;
  const displayMessage = message && typeof t === "function" ? t(message) : message;

  const overlay =
    show && typeof document !== "undefined" && document.body
      ? createPortal(
          <div className="loading-overlay-portal">
            <BallmatesFullPageLoader message={displayMessage} t={t} />
          </div>,
          document.body
        )
      : null;

  return (
    <>
      {children}
      {overlay}
    </>
  );
}
